/**
 * <mp-icon> — ícone SVG em LIGHT DOM, substituto do `<ion-icon src="…">` para os SVGs
 * custom de `assets/icon`.
 *
 * POR QUÊ: o `ion-icon` é um web component com SHADOW DOM; ele busca o SVG e o injeta no
 * shadow, dimensionado por CSS do `:host` (que vive em adoptedStyleSheets do shadow).
 * NENHUM player de session replay (OpenReplay, rrweb) reconstrói o CSS do shadow, então o
 * SVG renderiza no seu tamanho INTRÍNSECO — e os nossos custom (delivery/store/…) têm
 * `width="512"`, virando um ícone gigante na gravação. (Medido 28/08/2026; ver OPENREPLAY.md.)
 *
 * A cura, sem mexer no visual: inlinar o SVG em DOM normal. O conteúdo entra na árvore que o
 * replay grava; o tamanho vem da classe global `.mp-icon` (1em, content-box — idêntica ao
 * default do ion-icon); a cor segue `currentColor` (silhueta preta por padrão, branca quando
 * a linha selecionada inverte a cor do texto — paridade com o `fill: currentColor` do ion-icon).
 * Como o SVG fica INLINE, o replay não depende de refetch do asset — funciona inclusive fora de
 * produção. É o equivalente-ícone do shell-inline que curou o ion-modal do troco.
 *
 * Registrado como custom element em `main.ts` (ao lado do `defineIonicons`). Aceito nos
 * templates via `CUSTOM_ELEMENTS_SCHEMA` (já presente nos módulos), sem declaração.
 */
const svgCache = new Map<string, Promise<string>>();

function carregarSvg(url: string): Promise<string> {
	let p = svgCache.get(url);
	if (!p) {
		p = fetch(url)
			.then((r) => (r.ok ? r.text() : ''))
			.catch(() => '');
		svgCache.set(url, p);
	}
	return p;
}

export class MpIconElement extends HTMLElement {
	static get observedAttributes(): string[] {
		return ['src', 'name', 'color', 'size'];
	}

	private urlRenderizada = '';
	private corPorAtributo = false;

	// Reflete propriedades → atributos, para os bindings do Angular ([name]="…", [src]="…")
	// dispararem o attributeChangedCallback (Angular seta a PROPRIEDADE no custom element).
	get name(): string | null { return this.getAttribute('name'); }
	set name(v: string | null) { v == null ? this.removeAttribute('name') : this.setAttribute('name', v); }
	get src(): string | null { return this.getAttribute('src'); }
	set src(v: string | null) { v == null ? this.removeAttribute('src') : this.setAttribute('src', v); }
	get color(): string | null { return this.getAttribute('color'); }
	set color(v: string | null) { v == null ? this.removeAttribute('color') : this.setAttribute('color', v); }
	get size(): string | null { return this.getAttribute('size'); }
	set size(v: string | null) { v == null ? this.removeAttribute('size') : this.setAttribute('size', v); }

	connectedCallback(): void {
		this.render();
	}

	attributeChangedCallback(): void {
		if (this.isConnected) this.render();
	}

	private async render(): Promise<void> {
		// tamanho: a primitiva global .mp-icon (1em, content-box) — igual ao ion-icon.
		this.classList.add('mp-icon');
		const size = this.getAttribute('size');
		this.classList.toggle('mp-icon--small', size === 'small');
		this.classList.toggle('mp-icon--large', size === 'large');

		// cor: `color="warning"` → var do design system. Só escrevemos quando o atributo
		// `color` existe — NUNCA no `else`, senão apagaríamos a cor que o autor pôs no
		// `style="color:…"` inline (ex.: os +/- do carrinho, remove-circle/add-circle). A
		// remoção do atributo limpa o que ESTE código escreveu, não o style do autor.
		const color = this.getAttribute('color');
		if (color) {
			this.style.color = `var(--ion-color-${color}, currentColor)`;
			this.corPorAtributo = true;
		} else if (this.corPorAtributo) {
			this.style.color = '';
			this.corPorAtributo = false;
		}

		// origem: `src` direto, ou `assets/ionicons/<name>.svg` (best-effort para name=).
		const src = this.getAttribute('src');
		const name = this.getAttribute('name');
		const url = src || (name ? `assets/ionicons/${name}.svg` : '');
		if (!url) {
			this.innerHTML = '';
			this.urlRenderizada = '';
			return;
		}
		if (url === this.urlRenderizada) return;
		this.urlRenderizada = url;

		const txt = await carregarSvg(url);
		if (this.urlRenderizada !== url) return; // atributo mudou durante o fetch
		if (!txt || txt.indexOf('<svg') === -1) {
			this.innerHTML = '';
			return;
		}
		this.innerHTML = txt;
		const svg = this.querySelector('svg');
		if (svg) {
			// tamanho vem da classe .mp-icon (host) + CSS `.mp-icon > svg`, não do intrínseco
			// do arquivo (delivery.svg tem width="512"). fill/stroke idem, via CSS global — para
			// os paths .ionicon-fill-none seguirem vazados (outline), sem fill inline sobrepondo.
			svg.removeAttribute('width');
			svg.removeAttribute('height');
			svg.setAttribute('aria-hidden', 'true');
		}
	}
}

export function registrarMpIcon(): void {
	if (typeof customElements !== 'undefined' && !customElements.get('mp-icon')) {
		customElements.define('mp-icon', MpIconElement);
	}
}
